import { logger } from 'lib/hatchd-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthPageLayout } from 'components/auth-page-layout/auth-page-layout';
import { Layout } from 'components/layout';
import { Link } from 'components/link';
import { Button, Input, Stack } from 'components/ui';
import { usePwaConfig } from 'hooks/use-pwa-config';
import { NextPage } from 'next';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FIRST_LOGIN_STORAGE_KEY, PATHS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import * as yup from 'yup';

interface LoginPageProps {}

type FormData = {
  email: string;
  password: string;
};

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  password: yup.string().required('Please enter your password'),
});

const LinkGrid = styled.div<{ $isPwa?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[0]};
  width: ${({ $isPwa }) => ($isPwa ? '100%' : 'max-content')};
  align-items: ${({ $isPwa }) => ($isPwa ? 'center' : 'flex-start')};

  @media screen and (min-width: ${breakpoints.medium}) {
    width: max-content;
  }
`;

const StyledPwaInput = styled(Input)`
  label {
    text-align: center;
    width: 100%;
    font-size: ${globalTheme.fontSizes[5]};
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    label {
      text-align: left;
      width: auto;
      font-size: unset;
    }
  }
`;

const StyledPwaButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;

  @media screen and (min-width: ${breakpoints.medium}) {
    width: max-content;
  }
`;

const LoginPage: NextPage<LoginPageProps> = () => {
  const router = useRouter();
  const { data: session } = useSession();
  const { config } = usePwaConfig();

  // Redirect once authenticated
  useEffect(() => {
    if (session?.accessToken && router.pathname === PATHS.auth.logIn) {
      const callbackQuery = router.query.callbackUrl as string | undefined;
      // callbackUrl is either a relative path (from our own links, e.g. the
      // public header) or an absolute URL (from next-auth's signIn()), which
      // needs the origin stripped before routing
      const callbackUrl = callbackQuery
        ? callbackQuery.startsWith('/')
          ? callbackQuery
          : `/${callbackQuery.split('/').splice(3).join('/')}`
        : PATHS.home;
      logger.info('Redirecting to:', callbackUrl);
      router.push(callbackUrl);
    }
  }, [router, session?.accessToken]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      const response = await signIn<'credentials'>('credentials', {
        username: data.email,
        password: data.password,
        redirect: false,
      });

      // Handle no account / validation error messaging
      if (
        response?.error === 'NO_ACTIVE_ACCOUNT' ||
        response?.error === 'VALIDATION_ERROR'
      ) {
        toast.error('Email or password incorrect');
      }

      // Redirect to verification page, the user is not yet verified
      if (response?.error === 'IS_VERIFIED_ERROR') {
        router.push(`${PATHS.auth.verification}?email=${data.email}`);
      }

      // Something else went wrong, show a generic message
      if (response?.error === 'CredentialsSignin') {
        logger.error(response);
        toast.error('Something has gone wrong, please try again later');
      }
    } catch (error) {
      logger.error(error);
    }
  };

  // Show toast message after successful and unsucessful verification
  useEffect(() => {
    if (router.query.verified === 'true') {
      toast.success('Email successfully verified');
      localStorage.setItem(FIRST_LOGIN_STORAGE_KEY, 'true');
    }
    if (router.query.verified === 'false') {
      toast.error(
        'Email has not been verified. Please check your email for the latest verification email from Agora Livestock'
      );
    }
  }, [router.query.verified]);

  const FormInput = config?.org_id ? StyledPwaInput : Input;
  const FormButton = config?.org_id ? StyledPwaButton : Button;

  return (
    <Layout meta={{ title: 'Login' }}>
      <AuthPageLayout>
        <form onSubmit={handleSubmit(onSubmit)} data-cy='login-form'>
          <Stack space={3}>
            <FormInput
              id='email'
              type='email'
              label='Email'
              placeholder='johndoe@email.com'
              state={errors.email && 'error'}
              message={errors.email?.message}
              {...register('email')}
              data-cy='email'
            />
            <FormInput
              id='password'
              type='password'
              label='Password'
              placeholder='Enter password'
              state={errors.password && 'error'}
              message={errors.password?.message}
              {...register('password')}
              data-cy='password'
            />
            <FormButton type='submit' isLoading={isSubmitting} data-cy='login'>
              Login
            </FormButton>
            <LinkGrid $isPwa={!!config?.org_id}>
              <Link href={PATHS.auth.forgotPassword}>
                <a data-cy='forgotPasswordButton'>Forgot password?</a>
              </Link>

              {!config?.org_id && (
                <Link href={PATHS.auth.signUp}>
                  <a data-cy='createAccountButton'>Create an account</a>
                </Link>
              )}
            </LinkGrid>
          </Stack>
        </form>
      </AuthPageLayout>
    </Layout>
  );
};

export default LoginPage;
